import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import Pane from "@components/pane/pane.component";
import Form from "@components/form";
import {IonCol, IonLoading, IonRow, IonSelect, IonSelectOption} from "@ionic/react";
import {StyledInput} from "@components/form/input/input.style";
import {StyledContent} from "@components/accordion/accordion.style";

import moment, {ISO_8601, Moment} from "moment/moment";
import {
    getReportApplicationPreview,
} from "@services/helpdesk/helpdesk.service";
import {StyledIonSelect} from "@components/form/input/select.style";
import DateInput from "@components/form/input/date.component";
import {useHistory} from "react-router-dom";
import TimeInput from "@components/form/input/time.component";
import {StyledTextarea} from "@components/form/input/textarea.style";
import {StyledReportApplicationFormContainer} from "@app/helpdesk/reportApplication/components/reportApplication.style";
import {Application} from "@app/helpdesk/reportApplication/panes/reportApplicationListPane.component";
import TravelAllowanceFormSkeleton from "@app/travelAllowance/common/components/travelAllowanceFormSkeleton.component";


type ReportApplicationPaneProps = {
    topEdge?: number;
    applicationId: number
}

const ReportApplicationPreviewPane: React.FC<ReportApplicationPaneProps> = ({
                                                                                topEdge,
                                                                                applicationId
                                                                            }: ReportApplicationPaneProps) => {
    const {t} = useTranslation();
    const history = useHistory();

    const [loading, setLoading] = useState<boolean>(false);
    const [application, setApplication] = useState<Application>();

    useEffect(() => {
        setLoading(true);

        fetchApplication()
            .then(() => {
                setLoading(false)
            })
            .catch((e) => {
                console.error(e);
            });
    }, []);

    const fetchApplication = async () => {
        if (applicationId !== undefined) {

            const applicationData = await getReportApplicationPreview(applicationId);
            setApplication(applicationData.data);
        }
    }

    return (
        <Pane topEdge={topEdge} marginTop={40} paddingBottom={147}>
            <StyledContent>
                <StyledReportApplicationFormContainer>
                    {
                        loading &&
                        <TravelAllowanceFormSkeleton/>
                    }
                    {
                        !loading && application && <Form.Container>
                            <IonRow>
                                <IonCol size="12" className="label mt-8">
                                    {t('helpdesk.reportApplication.createdAt')}
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="select">
                                    <StyledInput
                                                 name="report"
                                                 placeholder={t('helpdesk.reportApplication.none')}
                                                 value={moment(application.createdAt, moment.ISO_8601).format('DD-MM-YYYY HH:mm:ss')}
                                    />
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label mt-8 required">
                                    {t('helpdesk.reportApplication.type')}
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label">
                                    <StyledIonSelect
                                        name='type'
                                        value={application.type}
                                        placeholder={t('common.selectPlaceholder')}
                                        okText={t('common.selectOk')}
                                        cancelText={t('common.selectCancel')}
                                        interfaceOptions={
                                            {
                                                header: t('helpdesk.reportApplication.type_select'),
                                                cssClass: 'alert-separators'
                                            }
                                        }
                                        className={'border padding-left'}
                                        style={{ pointerEvents: 'none' }}
                                    >
                                        <IonSelectOption
                                            value="TOO_FEW_OR_NO_HOURS"> {t('helpdesk.reportApplication.type_too_few_or_no_hours')}</IonSelectOption>
                                        <IonSelectOption
                                            value="TOO_MANY_HOURS"> {t('helpdesk.reportApplication.type_too_many_hours')}</IonSelectOption>
                                    </StyledIonSelect>
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label mt-8 required">
                                    {t('helpdesk.reportApplication.hours_and_minutes')}
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label">
                                    <TimeInput
                                        disabled={true}
                                        value={moment()
                                            .startOf('day')
                                            .add(application.hours, 'minutes')
                                            .format()} // np. "2025-10-09T03:10:00+02:00"
                                        onChange={(val) => console.log(val)}
                                    />
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label mt-8 required">
                                    {t('helpdesk.reportApplication.date')}
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label">
                                    <DateInput
                                        disabled={true}
                                        presentation="date"
                                        format="DD.MM.YYYY"
                                        value={application.date}
                                        onChange={(value) => {
                                        }}
                                        cancelText={t("common.dateCancel")}
                                        doneText={t("common.dateDone")}
                                        placeholder={t("common.select")}
                                    />
                                    <input
                                        type="hidden"
                                        name="date"
                                        value={application.date}
                                    />
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label mt-8 required">
                                    {t('helpdesk.reportApplication.client')}
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="select">
                                    <StyledInput
                                        name="report"
                                        placeholder={t('helpdesk.reportApplication.clientPlaceholder')}
                                        value={application.client.name}
                                    />
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label mt-8">
                                    {t('helpdesk.reportApplication.order')}
                                </IonCol>
                            </IonRow>

                            <IonRow>
                                <IonCol size="12" className="select">
                                    <StyledInput disabled={!application.order?.formatted_number}
                                        name="report"
                                        placeholder={t('helpdesk.reportApplication.none')}
                                        value={application.order?.formatted_number ? application.order?.formatted_number : ''}
                                    />
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label mt-8">
                                    {t('helpdesk.reportApplication.report')}
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="select">
                                    <StyledInput disabled={!application.report?.formatted_number}
                                                 name="report"
                                                 placeholder={t('helpdesk.reportApplication.none')}
                                                 value={application.report?.formatted_number ? application.report?.formatted_number : ''}
                                    />
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label mt-8 required">
                                    {t('helpdesk.reportApplication.commentWorker')}
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label">
                                    <StyledTextarea name='commentWorker' readOnly={true} inputMode="text"
                                                    value={application.comment_worker} disabled={true}
                                                    className={'full-width resize-none small'}
                                    />
                                </IonCol>
                            </IonRow>
                        </Form.Container>
                    }

                </StyledReportApplicationFormContainer>
            </StyledContent>
        </Pane>
    );
};

export default ReportApplicationPreviewPane;